import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { SubscriberService } from "../../../services/SubscriberService";
import { useConfirm } from "../../../config/ConfirmProvider";
import { INewsletter } from "../../../models/Interfaces";
import Loading from "../../components/common/Loading";
import { CellProps, Column } from "react-table";
import moment from "moment";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEnvelopesBulk, faMagnifyingGlass, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import FilteredTable from "../../components/common/FilteredTable";
import AppModal from "../../components/common/AppModal";
import HTMLEditor, { IEditorMenu } from "../../components/common/HTMLEditor";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./newsletters.module.scss";

const Newsletters = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [newsletters, setNewsletters] = useState<Array<INewsletter>>([]);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [selectedNL, setSelectedNL] = useState<INewsletter | null>(null);
  const [previewNL, setPreviewNL] = useState<INewsletter | null>(null);

  const [sending, setSending] = useState(false);
  const [sendMessage, setSendMessage] = useState("");
  const sendInProgress = useRef(false);

  const subSvcRef = useRef<SubscriberService | null>(null);
  const nlFormRef = useRef<HTMLFormElement | null>(null);
  const nlFvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const formatDate = (value: string): string => value ? moment(value).format("DD-MM-YYYY") : "";

  const columns: Column<INewsletter>[] = useMemo(() => [
    {
      Header: "Name",
      accessor: "Name"
    },
    {
      Header: "Subject",
      accessor: "Subject"
    },
    {
      Header: "Date Added",
      id: "dateadded",
      Cell: ({ row }: CellProps<INewsletter>) => <span>{formatDate(row.original.DateAdded)}</span>
    },
    {
      Header: "Date Modified",
      id: "datemodified",
      Cell: ({ row }: CellProps<INewsletter>) => <span>{formatDate(row.original.DateModified)}</span>
    },
    {
      Header: "Status",
      id: "status",
      Cell: ({ row }: CellProps<INewsletter>) => (
        <span className={styles.statusBadge}>{row.original.StatusDescription}</span>
      )
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<INewsletter>) => (
        <div className="user-result-actions">
          <Button variant="light" onClick={() => editNewsletter(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit newsletter">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => previewNewsletter(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Preview newsletter">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </Button>
          <Button variant="light" onClick={() => sendNewsletter(row.original)} disabled={sending} aria-busy={sending} className="user-result-actions__btn user-result-actions__btn--view" title="Send newsletter">
            <FontAwesomeIcon icon={faEnvelopesBulk} />
          </Button>
          <Button variant="light" onClick={() => deleteNewsletter(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete newsletter">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [sending]);

  const getNewsletters = async (): Promise<void> => {
    if (subSvcRef.current) {
      const res = await subSvcRef.current.getNewsletters();
      setNewsletters(res);
    }
  };

  const addNewsletter = (): void => {
    if (subSvcRef.current) {
      const nl = subSvcRef.current.newNewsletter();
      if (nl) {
        setSelectedNL(nl);
        setShowDetail(true);
      }
    }
  };

  const editNewsletter = (letter: INewsletter): void => {
    setSelectedNL(letter);
    setShowDetail(true);
  };

  const saveNewsletter = async (): Promise<void> => {
    if (subSvcRef.current && nlFvRef.current && selectedNL) {
      const validres = await nlFvRef.current.validate();

      if (validres === "Valid") {
        const saveres = await subSvcRef.current.saveNewsletter(selectedNL);

        if (saveres) {
          setNewsletters(prev =>
            prev.some(nl => nl.Id === saveres.Id)
              ? prev.map(nl => (nl.Id === saveres.Id ? saveres : nl))
              : [...prev, saveres]
          );
        }
        closeDetail();
      }
    }
  };

  const previewNewsletter = (_letter: INewsletter): void => {
    setPreviewNL(_letter);
    setShowPreview(true);
  };

  const sendNewsletter = async (letter: INewsletter): Promise<void> => {
    if (!letter || !subSvcRef.current || sendInProgress.current) return;
    sendInProgress.current = true;
    setSending(true);
    setSendMessage("");
    try {
      const queued = await subSvcRef.current.sendNewsletter(letter);
      if (queued) {
        setSendMessage("Newsletter queued for delivery.");
        await getNewsletters();
      } else {
        setSendMessage("Newsletter could not be queued. Please try again.");
      }
    } catch {
      setSendMessage("Delivery status could not be confirmed. Refresh the list before trying again.");
    } finally {
      sendInProgress.current = false;
      setSending(false);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedNL, (item) => item.Id, () => closeDetail());

  const closeDeletedPreview = useCloseDeletedSelection(previewNL, (item) => item.Id, () => closePreview());

  const deleteNewsletter = async (letter: INewsletter): Promise<void> => {
    if (subSvcRef.current && letter) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${letter.Name}</b> from your newsletters?<br /><b>This action cannot be undone.</b>`, "Delete Newsletter");

      if (confirmed) {
        const res = await subSvcRef.current.deleteNewsletter(letter.Id);

        if (res) {
          setNewsletters(prev => prev.filter(nl => nl.Id !== letter.Id));
          closeDeletedSelection(letter.Id);
          closeDeletedPreview(letter.Id);
        }
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedNL) {
      const { name, value } = event.target;
      if (name) {
        setSelectedNL(prev => ({
          ...prev as INewsletter,
          [name]: value
        }));
      }
    }
  };

  const createMailMergeItems = (): Array<IEditorMenu> => [
    {
      Name: "dataFields",
      ButtonText: "Data Fields",
      Items: [
        { text: "Firstname", insertText: "[FIRSTNAME]", onAction: (editorInst: any) => { editorInst.insertContent("[FIRSTNAME]"); } },
        { text: "Lastname", insertText: "[LASTNAME]", onAction: (editorInst: any) => { editorInst.insertContent("[LASTNAME]"); } },
        { text: "Site Link", insertText: "[SITEURL]", onAction: (editorInst: any) => { editorInst.insertContent("[SITEURL]"); } },
        { text: "Today's Date", insertText: "[TODAY]", onAction: (editorInst: any) => { editorInst.insertContent("[TODAY]"); } },
        { text: "Unsubscribe Link", insertText: "[UNSUBSCRIBE]", onAction: (editorInst: any) => { editorInst.insertContent("[UNSUBSCRIBE]"); } },
      ]
    }
  ];

  const closeDetail = (): void => {
    setSelectedNL(null);
    setShowDetail(false);
  };

  const closePreview = (): void => {
    setPreviewNL(null);
    setShowPreview(false);
  };

  const createValidator = (): void => {
    if (nlFormRef.current && !nlFvRef.current) {
      nlFvRef.current = createFormValidation(nlFormRef.current, {
        fields: {
          Subject: {
            validators: {
              notEmpty: { message: "Subject for email is required" }
            }
          },
          Name: {
            validators: {
              notEmpty: { message: "Newsletter name is required" }
            }
          },
          BodyHtml: {
            validators: {
              notEmpty: { message: "Newsletter content is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger()
        }
      });
    }
  };

  useEffect(() => {
    if (nlFormRef.current) {
      createValidator();
    } else {
      nlFvRef.current = null;
    }
  }, [selectedNL, showDetail]);

  useEffect(() => {
    if (!subSvcRef.current) {
      subSvcRef.current = new SubscriberService();
    }

    const getData = async () => {
      try {
        await getNewsletters();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading newsletters..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Newsletter Library</h2>
                </div>
                <div className={styles.headerActions}>
                  <Button variant="primary" onClick={addNewsletter}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add Newsletter
                  </Button>
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
              {sending && <p role="status">Queuing newsletter...</p>}
              {sendMessage && <p role="status">{sendMessage}</p>}
              {newsletters.length > 0 ? (
                <FilteredTable<INewsletter>
                  columns={columns}
                  data={newsletters}
                  noDataMessage="No newsletters found for your site."
                  showAddButton={true}
                  addButtonText="Add Newsletter"
                  addButtonIcon={faPlus}
                  addButtonHandler={addNewsletter}
                  cellClass="align-middle"
                  filterPlaceHolder="filter newsletters..."
                />
              ) : (
                <div className={styles.emptyWrap}>
                  <AppEmptyState
                    title="No newsletters found"
                    message="Create your first newsletter to start building subscriber communications for this site."
                    compact
                  />
                  <div className={styles.emptyActions}>
                    <Button variant="primary" onClick={addNewsletter}>
                      <FontAwesomeIcon icon={faPlus} />
                      Add Newsletter
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedNL && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="lg"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Newsletter Content"
          onDataAction={saveNewsletter}
          fullScreen={true}>
          <Form id="newsletter-form" ref={nlFormRef}>
            <Row>
              <Col md={12} lg={6} className="mt-3">
                <FormLabel htmlFor="Name">Newsletter Name</FormLabel>
                <FormControl
                  type="text"
                  value={selectedNL.Name}
                  onChange={handleInputChange}
                  name="Name"
                  id="Name"
                  title="Newsletter name"
                  placeholder="Add a name for the newsletter"
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={12} className="mt-3">
                <FormLabel htmlFor="Subject">Email Subject</FormLabel>
                <FormControl
                  type="text"
                  value={selectedNL.Subject}
                  onChange={handleInputChange}
                  name="Subject"
                  id="subject"
                  title="subject for the email"
                  placeholder="Add a subject for this newsletter"
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel htmlFor="BodyHtml">Newsletter Body</FormLabel>
                <HTMLEditor preview={false} documentKey={selectedNL?.Id}
                  value={selectedNL.BodyHtml}
                  onInputChange={handleInputChange}
                  name="BodyHtml"
                  id="BodyHtml"
                  menuItems={createMailMergeItems()}
                  height={700}
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}

      {showPreview && previewNL && (
        <AppModal
          onClose={closePreview}
          showButtons={true}
          size="xl"
          cancelButtonText="Close"
          cancelButtonVariant="primary"
          title="Newsletter Preview">
          <div className={styles.previewShell}>
            <div className={styles.previewFrame}>
              <div className={styles.previewHeader}>
                <div className={styles.previewEyebrow}>Newsletter preview</div>
                <h3 className={styles.previewTitle}>{previewNL.Subject || previewNL.Name || "Untitled newsletter"}</h3>
                <div className={styles.previewMeta}>
                  This preview shows the formatted content as subscribers would receive it. Merge fields remain as placeholders.
                </div>
              </div>

              <div className={styles.previewBody}>
                <div
                  className={styles.previewContent}
                  dangerouslySetInnerHTML={{ __html: previewNL.BodyHtml || "<p>No newsletter content added yet.</p>" }}
                />
              </div>
            </div>
          </div>
        </AppModal>
      )}
    </>
  );
};

export default Newsletters;
